/**
 * The intake answered before the account existed, settled after sign-in
 * (`D262`). Which of the two happens is `fateOfGuestAnswers` in
 * `features/auth/gate.ts`; this file is what each one does to the device.
 *
 * Plain async functions over injected ports, outside any component: the error
 * handling needs `try`, and React Compiler skips a component that has one
 * (`T-168`). Tested in Node (`__tests__/hand-off.test.ts`).
 *
 * ── ADOPT: THE ANSWERS BECOME THE ACCOUNT'S ────────────────────────────────
 * Run by the stepper as it opens for an account whose intake is unfinished —
 * the only place a commit can run. The guest record is copied into the
 * account's own record (`rememberIntake`, scoped to the account, removed by
 * the per-account cleanup like any other) and only then removed, so a process
 * killed in between finds the answers in one place or the other, never in
 * neither.
 *
 * The steps answered as a guest are reported here, once, in path order
 * (`onboarding_step_completed`, `D238`): the route is authenticated and the
 * event carries the account, so nothing could be reported before. They are
 * reported when the record is CONSUMED, and only then — a second open finds
 * no guest record and reports nothing.
 *
 * Without an account id (`GET /auth/me` has not answered), the guest record
 * is read but neither copied nor consumed: there is nothing to scope the copy
 * to, and the next open adopts it properly.
 *
 * ── DISCARD: A FINISHED PROFILE IS NEVER OVERWRITTEN ───────────────────────
 * Run by the gate when the account turns out to have finished its intake.
 * The record is removed, and the caller tells the person only when it held
 * an answer — an intake left on its first question lost nothing.
 */
import {
  forgetGuestIntake,
  type IntakeAnswers,
  type IntakeResumption,
  type IntakeStep,
  type IntakeStore,
  pathFor,
  readGuestIntake,
  rememberIntake,
} from "@repo/client/onboarding";
import { isStepAnswered } from "@repo/contract/training";

/** The steps on the path that hold an answer, in path order. */
export function answeredSteps(answers: IntakeAnswers): readonly IntakeStep[] {
  return pathFor(answers).filter((step) => isStepAnswered(answers, step));
}

export interface AdoptPorts {
  /** `null` while `GET /auth/me` has no answer: read, but do not consume. */
  readonly accountId: string | null;
  /** `onboarding_step_completed` for each step, fire-and-forget. */
  readonly reportSteps: (steps: readonly IntakeStep[]) => void;
  readonly store: IntakeStore;
}

/** The guest's intake as the account's, or `null` when there is none. */
export async function adoptGuestIntake(
  ports: AdoptPorts
): Promise<IntakeResumption | null> {
  const guest = await readGuestIntake(ports.store);
  if (guest === null || ports.accountId === null) {
    return guest;
  }
  await rememberIntake(ports.accountId, guest.answers, guest.step, ports.store);
  const steps = answeredSteps(guest.answers);
  if (steps.length > 0) {
    ports.reportSteps(steps);
  }
  await forgetGuestIntake(ports.store);
  return guest;
}

/**
 * Drop the guest's intake. `true` when it held at least one answer, which is
 * when the person is told their answers were not used.
 */
export async function discardGuestIntake(store: IntakeStore): Promise<boolean> {
  const guest = await readGuestIntake(store);
  await forgetGuestIntake(store);
  return guest !== null && answeredSteps(guest.answers).length > 0;
}
